/**
 * Helpers pour la gestion des conditions de graphiques
 *
 * Ce module contient les fonctions pour évaluer les conditions de visibilité
 * des graphiques, datasets et messages dans le module Data.
 */

import type { Source, BuildingSource, ConditionBlock, Building } from "@types"
import * as _ from "lodash-es"
import { useAuthStore } from "@core/stores/auth.store"
import { getValidator } from "./validators/registry"

/**
 * Récupère la valeur d'un champ dans un objet, même si le champ est imbriqué (ex: "a.b.c")
 * @param obj - objet dans lequel chercher la valeur
 * @param path - chemin vers la valeur (ex: "a.b.c")
 * @returns La valeur trouvée ou undefined
 */
function getValueByPath(obj: any, path: string) {
    const fieldParts = path.split(".")
    let currentValue: any = obj

    for (let i = 0; i < fieldParts.length; i++) {
        if (Array.isArray(currentValue)) {
            // Récupère toutes les valeurs du champ suivant dans le tableau
            currentValue = currentValue.map((item) => item[fieldParts[i]])
        } else {
            currentValue = currentValue?.[fieldParts[i]]
        }
    }

    // Si currentValue est un tableau imbriqué, aplatit
    const valueField = Array.isArray(currentValue)
        ? _.flattenDeep(currentValue)
        : currentValue

    return valueField
}

/**
 * Construit un contexte minimal pour les validators custom
 * @param data - Les données (building ou sources)
 * @returns Le contexte minimal
 */
function buildMinimalContext(
    data: Building | (Source | BuildingSource)[]
): any {
    if (data && typeof data === "object" && "_id_building" in data) {
        return { building: data }
    }
    return { sources: data }
}

/**
 * Gère la condition de type "min_match_in_list"
 * @param data - Les données (building ou sources)
 * @param condition - La condition à évaluer
 * @param getValueFn - Fonction pour récupérer la valeur du champ
 * @returns true si la condition est respectée, false sinon
 */
function handleMinMatchInList(
    data: Building | (Source | BuildingSource)[],
    condition: ConditionBlock,
    getValueFn: (item: any, field: string) => any
): boolean {
    // Vérifie que la condition est de type object avec list et min
    if (
        !condition.value ||
        typeof condition.value !== "object" ||
        !Array.isArray((condition.value as any)?.list) ||
        typeof (condition.value as any)?.min !== "number"
    ) {
        console.error(
            "La condition min_match_in_list doit être un objet avec une liste et un minimum"
        )
        return false
    }

    if (!condition.field) {
        console.error(
            "La condition min_match_in_list nécessite un champ 'field'"
        )
        return false
    }

    const list = (condition.value as any).list as (string | number)[]
    const min = (condition.value as any).min as number

    // Si c'est un building, on traite comme un tableau avec un seul élément
    const items = Array.isArray(data) ? data : [data]

    // valueField est un tableau de valeurs de idtype rencontrées
    const found = new Set(
        items
            // Récupère toutes les valeurs du champ dans tous les items
            .flatMap((item) => {
                return getValueFn(item, condition.field!)
            })
            // Garde uniquement les valeurs présentes dans la liste
            ?.filter((value) => list.includes(value)) as (string | number)[]
    )

    // Vérifie que le nombre d'éléments distincts trouvés est supérieur ou égal au minimum
    return found.size >= min
}

/**
 * Évalue une condition standard (non custom, non min_match_in_list)
 * @param valueField - La valeur du champ récupérée
 * @param condition - La condition à évaluer
 * @returns true si la condition est respectée, false sinon
 */
function evaluateStandardCondition(
    valueField: any,
    condition: ConditionBlock
): boolean {
    if (condition.value === undefined) {
        console.error(
            "La condition nécessite une valeur pour les opérateurs standards"
        )
        return false
    }

    switch (condition.operator) {
        case "map_inclue":
            return (valueField as any[]).includes(condition.value)
        case "inclue":
            return (condition.value as any[]).includes(valueField)
        case "array_min_length":
            return valueField?.length >= condition.value
        case "exists":
            return !!valueField
        case "not_eq":
            return valueField !== condition.value
        default:
            return valueField === condition.value
    }
}

/**
 * Vérifie qu'une condition donnée est respectée dans les sources.
 *
 * Dans le cas d'une condition de type "min_match_in_list", la vérification est faite sur l'ensemble des sources.
 * Dans les autres cas, la vérification est faite si au moins une source respecte la condition.
 * Supporte également les opérateurs custom via le système de registre.
 *
 * @param sources - Liste des sources à vérifier
 * @param condition - condition à vérifier
 * @returns true si la condition est respectée, false sinon
 */
export function handleSourceCondition(
    sources: (Partial<Source> | Partial<BuildingSource>)[],
    condition: ConditionBlock
): boolean {
    // Si opérateur custom, utiliser le registre
    if (condition.operator === "custom") {
        if (!condition.validatorId) {
            console.error("validatorId requis pour operator 'custom'")
            return false
        }

        const validatorWithContext = getValidator(condition.validatorId)
        if (!validatorWithContext) {
            console.error(
                `Validator '${condition.validatorId}' non trouvé dans le registre`
            )
            return false
        }

        // Construire le contexte (utiliser buildContext si fourni, sinon contexte minimal)
        const context = validatorWithContext.buildContext
            ? validatorWithContext.buildContext(sources, condition)
            : buildMinimalContext(sources)

        return validatorWithContext.validator(condition.value, context)
    }

    // Dans le cas d'une opération de type min_match_in_list, on vérifie dans la liste des sources
    if (condition.operator === "min_match_in_list") {
        return handleMinMatchInList(sources, condition, (source, field) =>
            getValueByPath(source, field)
        )
    }

    // Pour les autres opérateurs, vérifier si au moins une source respecte la condition
    return sources.some((source) => {
        if (!condition.field) {
            console.error(
                "Le champ 'field' est requis pour les opérateurs standards"
            )
            return false
        }

        const valueField = getValueByPath(source, condition.field)
        return evaluateStandardCondition(valueField, condition)
    })
}

/**
 * Vérifie qu'une condition donnée est respectée dans le bâtiment.
 *
 * Supporte les opérateurs standards, min_match_in_list et les opérateurs custom via le système de registre.
 *
 * @param building - Le bâtiment à vérifier
 * @param condition - La condition à vérifier
 * @returns true si la condition est respectée, false sinon
 */
export function handleBuildingCondition(
    building: Building,
    condition: ConditionBlock
): boolean {
    // Si opérateur custom, utiliser le registre
    if (condition.operator === "custom") {
        if (!condition.validatorId) {
            console.error("validatorId requis pour operator 'custom'")
            return false
        }

        const validatorWithContext = getValidator(condition.validatorId)

        if (!validatorWithContext) {
            console.error(
                `Validator '${condition.validatorId}' non trouvé dans le registre`
            )
            return false
        }

        // Construire le contexte (utiliser buildContext si fourni, sinon contexte minimal)
        const context = validatorWithContext.buildContext
            ? validatorWithContext.buildContext(building, condition)
            : buildMinimalContext(building)

        return validatorWithContext.validator(condition.value, context)
    }

    // Dans le cas d'une opération de type min_match_in_list
    if (condition.operator === "min_match_in_list") {
        return handleMinMatchInList(building, condition, (item, field) =>
            getValueByPath(item, field)
        )
    }

    // Logique standard existante
    if (!condition.field) {
        console.error(
            "Le champ 'field' est requis pour les opérateurs standards"
        )
        return false
    }

    const valueField = getValueByPath(building, condition.field)
    return evaluateStandardCondition(valueField, condition)
}

/**
 * Arbre minimal pour les chemins `field` des conditions d’origine utilisateur (store auth).
 */
function getAuthConditionRoot(): Record<string, unknown> {
    const auth = useAuthStore()
    return {
        subscription: auth.subscription,
        role: auth.role,
        companyId: auth.companyId,
    }
}

/**
 * Contexte passé aux validators custom lorsque `origin === "user"`.
 */
function buildUserMinimalContext(): { auth: ReturnType<typeof useAuthStore> } {
    return { auth: useAuthStore() }
}

/**
 * Évalue une condition basée sur l’utilisateur / l’entreprise connectés (store auth).
 * À utiliser lorsque `condition.origin === "user"`.
 */
export function handleUserCondition(condition: ConditionBlock): boolean {
    if (condition.operator === "custom") {
        if (!condition.validatorId) {
            console.error("validatorId requis pour operator 'custom'")
            return false
        }

        const validatorWithContext = getValidator(condition.validatorId)
        if (!validatorWithContext) {
            console.error(
                `Validator '${condition.validatorId}' non trouvé dans le registre`
            )
            return false
        }

        const context = validatorWithContext.buildContext
            ? validatorWithContext.buildContext(
                  getAuthConditionRoot() as unknown as Building,
                  condition
              )
            : buildUserMinimalContext()

        return validatorWithContext.validator(condition.value, context)
    }

    if (condition.operator === "min_match_in_list") {
        return handleMinMatchInList(
            getAuthConditionRoot() as unknown as Building,
            condition,
            (item, field) => getValueByPath(item, field)
        )
    }

    if (!condition.field) {
        console.error(
            "Le champ 'field' est requis pour les opérateurs standards"
        )
        return false
    }

    const valueField = getValueByPath(getAuthConditionRoot(), condition.field)
    return evaluateStandardCondition(valueField, condition)
}
